import { styled } from "@mui/material/styles";
import {
    Box,
    BoxProps,
    InternalStandardProps,
    List,
    ListItemButton,
} from "@mui/material";
import { Layout } from "../Layout";
import { AppTypography } from "JS/React/Components/AppTypography";
import { useNavigate } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppHeader } from "JS/React/Components/AppHeader";
import { favoritesCategoriesToString } from "JS/Helpers";
import { useGetFavorites, useGlobalFavorites } from "JS/React/Hooks/Favorites";
import { useAudios, useGlobalAudios } from "JS/React/Hooks/Audio";
import React, { useMemo } from "react";
import { useGlobalVideos, useVideos } from "JS/React/Hooks/Video";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { uniqBy } from "lodash-es";
import {
    useGlobalGifts,
    useProspectingUser,
    useReceivedMemberAudioGifts,
    useReceivedMemberVideoGifts,
    useReceivedProspectAudioGifts,
    useReceivedProspectVideoGifts,
} from "JS/React/Hooks/Gifts";
import { messaging } from "JS/Helpers/UserMessaging";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { IconName, IconPrefix } from "@fortawesome/fontawesome-svg-core";
import { useGlobalDocuments } from "JS/React/Hooks/Documents";
import { AppDivider } from "JS/React/Components/AppDivider";

const StyledRoot = styled(Box)({});

const FavTopText = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

const StyledListItem = styled(ListItemButton)({
    fontWeight: 400,
    display: "flex",
    justifyContent: "flex-start",
});

const StyledList = styled(List)({
    flexGrow: 1,
});

const ListIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    fontWeight: 400,
    fontSize: "24px",
    marginRight: "5%",
    width: "10%",
    color: theme.palette.grey[600],
}));

const ListText = styled(AppTypography)(({ theme }) => ({
    fontWeight: 400,
    color: theme.palette.grey[600],
}));

export enum FavoritesCategories {
    AUDIOS = "Audios",
    VIDEOS = "Videos",
    DOCUMENTS = "Documents",
}

export enum FavoritesTypes {
    FAVAUDIOS = "favAudios",
    FAVVIDEOS = "favVideos",
}

type IconType = [IconPrefix, IconName];

export interface FavoritesProps extends InternalStandardProps<BoxProps> {}

export const Favorites = (props: FavoritesProps) => {
    const { className, ...rest } = props;

    const { audios } = useGlobalAudios();
    const { videos } = useGlobalVideos();
    const { resetFavoritesStack, homeStack } = useGlobalNavStack();

    const { loading: audiosLoading } = useAudios();

    const { isProsUser, loading: loadingProsStatus } = useProspectingUser();

    const { loading: videosLoading } = useVideos();

    const {
        receivedMemberAudios,
        receivedProspectAudios,
        receivedMemberVideos,
        receivedProspectVideos,
    } = useGlobalGifts();

    const { loading: receivedMemberVideoGiftsLoading } =
        useReceivedMemberVideoGifts(!!receivedMemberVideos?.length);

    const { loading: receivedProspectVideoGiftsLoading } =
        useReceivedProspectVideoGifts(
            !isProsUser || !!receivedProspectVideos?.length,
        );

    const { loading: receivedMemberAudioGiftsLoading } =
        useReceivedMemberAudioGifts(!!receivedMemberAudios?.length);

    const { loading: receivedProspectAudioGiftsLoading } =
        useReceivedProspectAudioGifts(
            !isProsUser || !!receivedProspectAudios?.length,
        );

    const { favNids } = useGlobalFavorites();
    const { loading: favLoading } = useGetFavorites(!!favNids?.length);

    const { documentUserFavorites } = useGlobalDocuments();

    const filteredAudios = useMemo(
        () =>
            uniqBy(
                [
                    ...audios,
                    ...(receivedMemberAudios || []),
                    ...(receivedProspectAudios || []),
                ],
                (x) => x.nid,
            ),
        [audios, receivedMemberAudios, receivedProspectAudios],
    );

    const filteredVideos = useMemo(
        () =>
            uniqBy(
                [
                    ...videos,
                    ...(receivedMemberVideos || []),
                    ...(receivedProspectVideos || []),
                ],
                (x) => x.nid,
            ),
        [audios, receivedMemberVideos, receivedProspectVideos],
    );

    const filterFavoritesAudios = useMemo(
        () => filteredAudios.filter((audio) => favNids?.includes(audio.nid)),
        [filteredAudios, favNids],
    );

    const filterFavoritesVideos = useMemo(
        () => filteredVideos.filter((video) => favNids?.includes(video.nid)),
        [filteredVideos, favNids],
    );

    const audiosContentLoaded =
        !audiosLoading &&
        !receivedMemberAudioGiftsLoading &&
        !receivedProspectAudioGiftsLoading &&
        !loadingProsStatus;

    const videosContentLoaded =
        !videosLoading &&
        !receivedMemberVideoGiftsLoading &&
        !receivedProspectVideoGiftsLoading &&
        !loadingProsStatus;

    const favoritesContentLoaded = !favLoading || favNids?.length !== 0;

    const handleItemClick = (item) => {
        if (item === FavoritesCategories.AUDIOS)
            navigate(
                linkProvider.react.favorites().favoriteAudiosListing(),
            );
        else if (item === FavoritesCategories.VIDEOS)
            navigate(
                linkProvider.react.favorites().favoriteVideosListing(),
            );
        else
            navigate(
                linkProvider.react.favorites().favoriteDocumentsListing(),
            );
    };

    const getListItemIcons = (item) => {
        if (item === FavoritesCategories.VIDEOS) {
            return ["fas", "video"] as IconType;
        } else if (item === FavoritesCategories.AUDIOS) {
            return ["fas", "headphones"] as IconType;
        } else {
            return ["far", "clipboard"] as IconType;
        }
    };

    const navigate = useNavigate();
    const { linkProvider } = useRouting();

    return (
        <Layout>
            <StyledRoot {...rest} className={className}>
                <AppHeader
                    title={"Favorites"}
                    canGoBack
                    onBackClick={() => {
                        if (!homeStack?.isHomeTrack) {
                            resetFavoritesStack();
                            navigate(linkProvider.react.more().index());
                        } else {
                            navigate(linkProvider.react.home());
                        }
                    }}
                    searchable={false}
                />
                <Box>
                    <FavTopText variant="body2" textAlign={"center"}>
                        You can mark your favorite media and have quick access
                        from this screen.
                    </FavTopText>
                </Box>
                <Box display={"flex"} flexDirection={"column"} height={"100%"}>
                    <StyledList>
                        {Object.values(FavoritesCategories).map((item) => (
                            <React.Fragment key={item}>
                                <StyledListItem
                                    onClick={() => handleItemClick(item)}
                                >
                                    <ListIcon icon={getListItemIcons(item)} />
                                    <ListText variant="h6">
                                        {favoritesCategoriesToString(item)}
                                        &nbsp;
                                        {((videosContentLoaded &&
                                            favoritesContentLoaded) ||
                                            filterFavoritesVideos?.length !==
                                                0) &&
                                            item ===
                                                FavoritesCategories.VIDEOS &&
                                            `(${filterFavoritesVideos.length})`}
                                        {((audiosContentLoaded &&
                                            favoritesContentLoaded) ||
                                            filterFavoritesAudios?.length !==
                                                0) &&
                                            item ===
                                                FavoritesCategories.AUDIOS &&
                                            `(${filterFavoritesAudios.length})`}
                                        {documentUserFavorites?.length !== 0 &&
                                            item ===
                                                FavoritesCategories.DOCUMENTS &&
                                            `(${documentUserFavorites?.length})`}
                                    </ListText>
                                </StyledListItem>
                                <AppDivider />
                            </React.Fragment>
                        ))}
                    </StyledList>
                </Box>
                {(!favoritesContentLoaded ||
                    !audiosContentLoaded ||
                    !videosContentLoaded) && (
                    <AppBackdropProgress
                        open={true}
                        backdropText={
                            !favoritesContentLoaded
                                ? messaging.loader.favorites
                                : !videosContentLoaded
                                ? messaging.loader.videos
                                : messaging.loader.audios
                        }
                    />
                )}
            </StyledRoot>
        </Layout>
    );
};
